/* eslint-disable no-param-reassign */
import { createSlice, PayloadAction } from '@reduxjs/toolkit'

import { get } from 'utils/lodash'

// Ephemeral multi-select state for the board-v2 bulk-move flow (slice 2.3).
// NOT persisted — selection is a transient UI mode, cleared when the user
// leaves it. Kept V2-native and lean rather than reusing the legacy
// Mapbox-coupled createBulkActionSlice.
//
// Selection is scoped to ONE column at a time: each column header has a "Bulk
// Moves" / "Bulk Assign" trigger; activating it sets that column as the active
// bulk column and shows checkboxes on its cards only. Switching to another
// column clears the prior selection.

export type BoardV2BulkState = {
  // routeId of the column currently in bulk mode, or null when no column is.
  activeRouteId: string | null
  // workOrderIds of the selected stops within the active column.
  selectedStopIds: string[]
  // whether the inline bulk-move panel is showing for the active column.
  moveOpen: boolean
}

const initialState: BoardV2BulkState = {
  activeRouteId: null,
  selectedStopIds: [],
  moveOpen: false,
}

const boardV2BulkSlice = createSlice({
  name: 'boardV2Bulk',
  initialState: { ...initialState },
  reducers: {
    // Enter bulk mode for a column (or switch columns). Always starts from an
    // empty selection so a stale set can't leak across columns. Passing null
    // exits bulk mode.
    setBoardV2BulkColumn: (state, action: PayloadAction<string | null>) => {
      state.activeRouteId = action.payload
      state.selectedStopIds = []
      state.moveOpen = false
    },
    // Show/hide the inline bulk-move panel within the active column.
    setBoardV2BulkMoveOpen: (state, action: PayloadAction<boolean>) => {
      state.moveOpen = action.payload
    },
    toggleBoardV2StopSelected: (state, action: PayloadAction<string>) => {
      const id = action.payload
      state.selectedStopIds = state.selectedStopIds.includes(id)
        ? state.selectedStopIds.filter(s => s !== id)
        : [...state.selectedStopIds, id]
    },
    // Replace the whole selection — "select all" passes the column's visible
    // stop ids.
    setBoardV2SelectedStops: (state, action: PayloadAction<string[]>) => {
      state.selectedStopIds = action.payload
    },
    // "Unselect all" — empty the selection without leaving bulk mode (so the
    // active column / checkboxes stay).
    clearBoardV2SelectedStops: state => {
      state.selectedStopIds = []
    },
  },
})

export const {
  setBoardV2BulkColumn,
  setBoardV2BulkMoveOpen,
  toggleBoardV2StopSelected,
  setBoardV2SelectedStops,
  clearBoardV2SelectedStops,
} = boardV2BulkSlice.actions

export const selectBoardV2BulkRouteId = (state: unknown): string | null =>
  (get(state, 'boardV2Bulk.activeRouteId') as string | null) ?? initialState.activeRouteId

export const selectBoardV2SelectedStopIds = (state: unknown): string[] =>
  (get(state, 'boardV2Bulk.selectedStopIds') as string[]) ?? initialState.selectedStopIds

export const selectBoardV2BulkMoveOpen = (state: unknown): boolean =>
  (get(state, 'boardV2Bulk.moveOpen') as boolean) ?? initialState.moveOpen

export default boardV2BulkSlice.reducer
